// crewbit-admin/src/components/money/proposals/ProposalPill.tsx
//
// Tier 2-UI Phase B Phase B (admin) — proposal-flow §0.13 override-discipline
// primitive. Mirrors crewbit-mobile/components/money/proposals/ProposalPill.tsx
// body decisions per `feedback_admin_pair_must_match_mobile_body` — same 3
// source variants, same tone semantics, same click-to-tooltip affordance.
// Web idiom uses inline-CSS chrome (admin convention) instead of StyleSheet.
//
// Source values map to ProposedCategorization.reasoning.source from the
// propose_expense_categorization RPC envelope:
//   - 'rule'           — pattern-matched a categorization rule
//   - 'ai_fallback'    — propose RPC fell back to categorize-expense edge function
//   - 'manual_override'— operator overrode the proposal (admin save fires
//                        confirm_with_overrides when picker selection differs
//                        from proposal default; §WorldClass.3 Phase B shipped)

import { palette, radii, spacing, typography } from '../../../styles/tokens';

export type ProposalPillSource = 'rule' | 'ai_fallback' | 'manual_override';

interface Props {
  source: ProposalPillSource;
  label?: string | null;
  onClick?: () => void;
}

interface Tone {
  bg: string;
  dot: string;
  fg: string;
}

const TONE: Record<ProposalPillSource, Tone> = {
  rule: {
    bg: palette.accentWash,
    dot: palette.accent,
    fg: palette.accentDeep,
  },
  ai_fallback: {
    bg: palette.purpleWash,
    dot: palette.purple,
    fg: palette.purple,
  },
  manual_override: {
    bg: palette.amberWash,
    dot: palette.amber,
    fg: palette.amber,
  },
};

const DEFAULT_LABEL: Record<ProposalPillSource, string> = {
  rule: 'Rule match',
  ai_fallback: 'AI suggestion',
  manual_override: 'Manual override',
};

export default function ProposalPill({ source, label, onClick }: Props) {
  const tone = TONE[source];
  const text = label ?? DEFAULT_LABEL[source];
  const interactive = !!onClick;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!interactive}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        background: tone.bg,
        color: tone.fg,
        border: 'none',
        borderRadius: radii.sm,
        paddingInline: spacing.xs,
        paddingBlock: 3,
        fontSize: 11,
        fontWeight: typography.weight.semibold,
        fontFamily: typography.fontFamily,
        cursor: interactive ? 'pointer' : 'default',
        maxWidth: 220,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: tone.dot,
          flexShrink: 0,
        }}
      />
      {text}
    </button>
  );
}
